/**
 * MoneyInput — a controlled input that displays a USD value as the user
 * types ("$3,000,000.00") while exposing a plain numeric string to the
 * parent ("3000000.00"). Drop-in replacement for the dollar-amount fields
 * that previously rendered a raw `<input className="field-input mono">`.
 *
 * State contract: `value` is the raw decimal string (no $, no commas, at
 * most one decimal point, at most 2 fractional digits). Empty string means
 * "no value entered". Parents continue to do `Number(value)` for math.
 */
import { useLayoutEffect, useRef } from 'react';

type Props = {
  value: string;
  onChange: (rawNumeric: string) => void;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  id?: string;
  style?: React.CSSProperties;
};

export function MoneyInput({
  value,
  onChange,
  disabled,
  placeholder,
  autoFocus,
  className,
  id,
  style,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const cursorTarget = useRef<number | null>(null);

  const display = formatForDisplay(value);

  useLayoutEffect(() => {
    if (cursorTarget.current != null && inputRef.current) {
      const pos = cursorTarget.current;
      inputRef.current.setSelectionRange(pos, pos);
      cursorTarget.current = null;
    }
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const oldCursor = e.target.selectionStart ?? e.target.value.length;
    const anchorsBefore = countAnchors(e.target.value.slice(0, oldCursor));
    const raw = sanitize(e.target.value);
    const newDisplay = formatForDisplay(raw);
    cursorTarget.current = positionAfterAnchors(newDisplay, anchorsBefore);
    onChange(raw);
  };

  return (
    <input
      ref={inputRef}
      id={id}
      className={className}
      style={style}
      value={display}
      onChange={handleChange}
      disabled={disabled}
      placeholder={placeholder ? `$${placeholder}` : '$0.00'}
      autoFocus={autoFocus}
      inputMode="decimal"
      autoComplete="off"
    />
  );
}

export function sanitize(s: string): string {
  // A single leading minus is meaningful — deduct/credit PCOs carry negative
  // amounts (e.g. 063 "Deduct of Central Laydown" at -$2M). Only a minus
  // before the first digit counts; anything else is a typo and drops out.
  const neg = /^[^0-9]*-/.test(s);
  let out = s.replace(/[^0-9.]/g, '');
  if (out === '') return neg ? '-' : '';
  const firstDot = out.indexOf('.');
  if (firstDot >= 0) {
    out = out.slice(0, firstDot + 1) + out.slice(firstDot + 1).replace(/\./g, '');
  }
  const dotIdx = out.indexOf('.');
  let whole: string;
  let frac: string | null = null;
  if (dotIdx >= 0) {
    whole = out.slice(0, dotIdx);
    frac = out.slice(dotIdx + 1).slice(0, 2);
  } else {
    whole = out;
  }
  const normWhole = (neg ? '-' : '') + (whole.replace(/^0+/, '') || '0');
  return frac !== null ? `${normWhole}.${frac}` : normWhole;
}

export function formatForDisplay(raw: string): string {
  if (raw === '') return '';
  // Mid-typing state: a bare minus renders as "$-" so the user can keep going.
  if (raw === '-') return '$-';
  const [whole, frac] = raw.split('.');
  const wholeNum = whole === '' || whole === '-' ? '0' : whole;
  const wholeFormatted = Number(wholeNum).toLocaleString('en-US');
  const result = frac !== undefined ? `${wholeFormatted}.${frac}` : wholeFormatted;
  return '$' + result;
}

/**
 * Caret anchors are the characters that survive both sanitize() and
 * formatForDisplay(): digits, the decimal point, and the leading minus.
 * The "$" and thousands commas are synthetic. Counting digits alone put the
 * restored caret BEFORE a just-typed "." ("$20|."), so the next keystroke
 * landed in the dollars ("$200.") — the dot must anchor the caret too.
 */
const ANCHOR = /[0-9.-]/;

export function countAnchors(s: string): number {
  return (s.match(/[0-9.-]/g) || []).length;
}

export function positionAfterAnchors(s: string, n: number): number {
  if (n === 0) return s.startsWith('$') ? 1 : 0;
  let count = 0;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch && ANCHOR.test(ch)) {
      count++;
      if (count === n) return i + 1;
    }
  }
  return s.length;
}
